import { PlusIcon } from "@heroicons/react/solid"
import { Breadcrumbs } from "components/breadcrumbs"
import Button from "components/button"
import { Link } from "react-router-dom"
import { AttractionQRCodeCard, AttractionQRCodeTable, Filter } from "./components"
import { useTable, useUpdate } from "@refinedev/core"
import { useMemo } from "react"
import Pagination from "components/pagination"
import { FullScreenLoading } from "components/fullScreenLoading"
import { IAttractionQRCodeItem } from "interfaces"
import toast from "react-hot-toast"
import { ExportButton } from "components/exportButton"
import { isHaveActionPermission } from "utills/helpers"

export const AttractionQRCodeList: React.FC = () => {
  const { mutate: updateMutation, isLoading: updateMutationIsLoading } = useUpdate();

  const {
    tableQuery,
    filters,
    setFilters,
  } = useTable<IAttractionQRCodeItem>({
    resource: "participant/ecodes",
    syncWithLocation: true,
    pagination: {
      mode: "off",
    },
    meta: {
      resource: "codes",
    },
  });

  const currentFilterValues = useMemo(() => {
    const logicalFilters = filters.flatMap((item) =>
      "field" in item ? item : []
    );

    return {
      q: logicalFilters.find((item) => item.field === "q")?.value || "",
      event_id: logicalFilters.find((item) => item.field === "event_id")?.value || "",
      status: logicalFilters.find((item) => item.field === "status")?.value || "",
      is_active: logicalFilters.find((item) => item.field === "is_active")?.value || "",
      ticket_id: logicalFilters.find((item) => item.field === "ticket_id")?.value || "",
    };
  }, [filters]);

  const _toggleActive = (id: string, isActive: boolean) => {
    if (!isHaveActionPermission("attractions_qr_code", "events", "edit")) return;
    if (window.confirm(`Are you sure you want to ${!isActive ? "activate": "deactivate"} this code?`)) {
      updateMutation({
        resource: "participant/ecodes",
        id,
        meta: {
          URLSuffix: `status/${!isActive}`,
          ignoreResourceWrapper: true
        },
        values: {},
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
        },
      });
    }
  };

  const _handlePageChanged = (page: number) => {
    const updatedFilters = [...filters].flatMap(item => "field" in item ? item: []);
    const pageIndex = updatedFilters.findIndex(filter => filter.field === "page");
    if (pageIndex < 0) {
      updatedFilters.push({field: "page", operator: "eq", value: page})
    } else {
      updatedFilters[pageIndex].value = page;
    }

    setFilters(updatedFilters);
  }
  
  return (
    <div className="wrapper">
      <Breadcrumbs
        additionalLabel={
          <div className="flex items-center gap-x-3">
            {isHaveActionPermission("attractions_qr_code", "events", "export") ? (
              <ExportButton
                filters={filters}
                title="Export code"
                resource="export/e-codes"
                className="w-fit bg-white text-brand-500 font-bold py-2 border-solid border-brand-500 border-[1px] hover:bg-white active:bg-white active:opacity-85"
              />
            ) : <></>}
            {isHaveActionPermission("attractions_qr_code", "events", "create") ? (
              <Link to={"/events/attraction-qr-code/create"}>
                <Button className="w-11 h-11 sm:w-auto">
                  <span className="sr-only">Create New code</span>
                  <PlusIcon className="w-4 h-4 text-white" />{" "}
                  <span className="hidden sm:block sm:ml-2">
                    Create New code
                  </span>
                </Button>
              </Link>
            ) : <></>}
          </div>
        }
      />
      <div className="box-container">
        <Filter
          currentFilterValues={currentFilterValues}
          setFilters={setFilters}
        />
      </div>
      <div className="box-container">
        {!tableQuery.data?.data?.length &&
          !tableQuery.isFetching && (
            <div className="text-center">
              No data
            </div>
          )}
        {tableQuery?.data?.data && tableQuery?.data?.data.length > 0 ? (
          <>
            <div className="sm:hidden">
              <AttractionQRCodeCard
                data={tableQuery?.data?.data}
                pageSize={tableQuery?.data?.pageSize}
                currentPage={tableQuery?.data?.paging.current_page}
                _toggleActive={_toggleActive}
              />
            </div>
            <div className="hidden sm:block">
              <AttractionQRCodeTable
                data={tableQuery?.data?.data}
                pageSize={tableQuery?.data?.pageSize}
                currentPage={tableQuery?.data?.paging.current_page}
                _toggleActive={_toggleActive}
              />
            </div>
          </>
        ) : <></>}
        {tableQuery?.data?.paging ? (
          <div className="mt-6">
            <Pagination
              currentPage={tableQuery?.data?.paging.current_page}
              totalPage={tableQuery?.data?.paging.total_page}
              total={{
                totalItems: tableQuery?.data?.paging?.total_item ?? 0,
                total: tableQuery?.data?.paging.total
              }}
              onChange={_handlePageChanged}
            />
          </div>
        ) : <></>}
      </div>
      {(tableQuery.isLoading) || (tableQuery.isFetching && tableQuery.isPreviousData)|| updateMutationIsLoading ? <FullScreenLoading /> : <></>}
    </div>
  )
}